// ============================================================================
// useWorkItemCustomerItems — list / add / update / remove per-customer
// contracted prices for a given work_item_id.
// Date: April 22, 2026
// ============================================================================

'use client';

import { useState, useEffect, useCallback } from 'react';
import { authedFetch } from '@/lib/authedFetch';

const fetchWithAuth = authedFetch;

export interface WorkItemCustomerItem {
  id: string;
  client_id: number;
  contact_id: number;
  work_item_id: string;
  customer_sku: string | null;
  customer_description: string | null;
  contracted_price: number | null;
  contract_currency: string;
  contract_start: string | null;
  contract_end: string | null;
  min_order_qty: number | null;
  lead_time_days: number | null;
  is_preferred: boolean;
  is_active: boolean;
  notes: string | null;
  // Joined fields from GET handler:
  item_title?: string;
  item_number?: string;
  item_sku?: string;
  item_base_selling_price?: number | string | null;
  item_currency?: string;
  contact_name?: string;
}

export interface CustomerItemCreatePayload {
  contact_id: number;
  work_item_id: string;
  customer_sku?: string | null;
  customer_description?: string | null;
  contracted_price?: number | null;
  contract_currency?: string;
  contract_start?: string | null;
  contract_end?: string | null;
  min_order_qty?: number | null;
  lead_time_days?: number | null;
  is_preferred?: boolean;
  is_active?: boolean;
  notes?: string | null;
}

const BASE = '/api/v1/customer-items';

export function useWorkItemCustomerItems(workItemId?: string) {
  const [items, setItems] = useState<WorkItemCustomerItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!workItemId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetchWithAuth(`${BASE}?work_item_id=${encodeURIComponent(workItemId)}&include_inactive=true`);
      const json = await res.json();
      if (!json.success) throw new Error(json.error || 'Failed to load customer pricing');
      setItems(json.data || []);
    } catch (e: any) {
      setError(e?.message || 'Failed to load customer pricing');
    } finally {
      setLoading(false);
    }
  }, [workItemId]);

  useEffect(() => {
    load();
  }, [load]);

  const create = useCallback(
    async (payload: CustomerItemCreatePayload) => {
      const res = await fetchWithAuth(BASE, { method: 'POST', body: JSON.stringify(payload) });
      const json = await res.json();
      if (!json.success) throw new Error(json.error || 'Failed to add customer pricing');
      await load();
      return json.data;
    },
    [load],
  );

  const update = useCallback(
    async (id: string, payload: Partial<WorkItemCustomerItem>) => {
      const res = await fetchWithAuth(`${BASE}/${id}`, { method: 'PUT', body: JSON.stringify(payload) });
      const json = await res.json();
      if (!json.success) throw new Error(json.error || 'Failed to update customer pricing');
      await load();
      return json.data;
    },
    [load],
  );

  const remove = useCallback(
    async (id: string) => {
      const res = await fetchWithAuth(`${BASE}/${id}`, { method: 'DELETE' });
      const json = await res.json();
      if (!json.success) throw new Error(json.error || 'Failed to remove customer pricing');
      await load();
    },
    [load],
  );

  return { items, loading, error, reload: load, create, update, remove };
}
